"use client";

import { usePathname } from "next/navigation";
import { isNavPillActive, NavPill } from "./NavPill";
import { type NavProfile, useNavContext, useNavProfile } from "./useNavAuth";

/**
 * The signed-in half of the desktop pill row, rendered as siblings of the
 * static public pills inside the shell's `<nav>`. Signed-out visitors get
 * nothing here, which is why the slot's fallback is empty.
 */
export function NavBarAuthPillsClient({
  profile: serverProfile,
  initialContext,
  driverFeaturesEnabled,
}: {
  profile: NavProfile;
  initialContext: "host" | "driver";
  driverFeaturesEnabled: boolean;
}) {
  const pathname = usePathname();
  const profile = useNavProfile(serverProfile);
  const currentContext = useNavContext({ profile, initialContext });

  const isOnboarding = pathname.startsWith("/host/onboarding");
  if (!profile || isOnboarding) return null;

  // Messages is NOT here: it lives in the user menu, because it is
  // context-independent (the inbox holds both sides of the account) and this
  // pill row swaps wholesale between host and driver links.
  const items =
    currentContext === "host"
      ? [
          { href: "/host/listings", label: "Listings" },
          { href: "/host/reservations", label: "Bookings" },
          // Payouts merged into Earnings — /host/payouts now redirects there.
          { href: "/host/earnings", label: "Earnings" },
        ]
      : driverFeaturesEnabled
        ? // No "Find a Spot" entry here: the shell already renders a permanent
          // "Find Parking" → /parking pill, and a second entry for the same
          // href rendered a duplicate pill.
          [{ href: "/driver/bookings", label: "Reservations" }]
        : [];

  return (
    <>
      {items.map((item) => (
        <NavPill
          key={item.href}
          {...item}
          active={isNavPillActive(pathname, item.href)}
        />
      ))}
    </>
  );
}
